// Repeat-billing plan detail: the schedule, its template lines, pause and
// resume, and the global operator run trigger (generates invoices for every
// due plan, not only this one).
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  RecurringPlanGenerate,
} from "@/components/RecurringPlanGenerate";
import {
  useAnyHeldPlanGenerate,
  useAnyPlanGeneratePending,
  usePlanGenerateScope,
} from "@/components/plan-generate-recovery";
import { CardField } from "@/components/RecordCard";
import {
  generateRecurringInvoices,
  getRecurringSchedule,
  pauseRecurringSchedule,
  type RecurringGenerateResult,
  type RecurringInvoiceSchedule,
} from "@/lib/api";
import { frequencyLabel, useAccountLabels } from "@/lib/labels";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "active":
      return "success";
    case "paused":
      return "warning";
    case "completed":
      return "idle";
    default:
      return "idle";
  }
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function describeRunResult(result: RecurringGenerateResult): string {
  if (result.generated_count === 0) {
    return "Nothing was due — no invoices generated.";
  }
  const noun = result.generated_count === 1 ? "invoice" : "invoices";
  const ids = result.generated.map((row) => row.invoice_id).filter(Boolean);
  return `Generated ${result.generated_count} ${noun} across all due plans.${
    ids.length > 0 ? ` Invoice ids: ${ids.join(", ")}.` : ""
  }`;
}

export function RecurringScheduleDetailPage() {
  const { recurringScheduleId } = useParams({ strict: false }) as {
    recurringScheduleId: string;
  };
  const [schedule, setSchedule] = useState<RecurringInvoiceSchedule | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(false);
  const accountLabels = useAccountLabels();
  const planScope = usePlanGenerateScope();
  const generateHeld = useAnyHeldPlanGenerate(planScope);
  const generatePending = useAnyPlanGeneratePending(planScope);

  const reload = useCallback(() => {
    if (!recurringScheduleId) return;
    setError(null);
    getRecurringSchedule(recurringScheduleId)
      .then(setSchedule)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [recurringScheduleId]);

  useEffect(reload, [reload]);

  const runNow = async () => {
    setRunning(true);
    setError(null);
    setNotice(null);
    try {
      const result = await generateRecurringInvoices();
      setNotice(describeRunResult(result));
      setConfirmRun(false);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
      setConfirmRun(false);
    } finally {
      setRunning(false);
    }
  };

  const toggleStatus = async () => {
    if (!recurringScheduleId || !schedule) return;
    const pausing = schedule.status.toLowerCase() === "active";
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await pauseRecurringSchedule(recurringScheduleId);
      setNotice(
        result.status === "paused"
          ? "Paused — this plan sits out of the run."
          : "Resumed — this plan is back in the run.",
      );
      setConfirmToggle(false);
      reload();
    } catch (cause) {
      setError(
        `${pausing ? "Pause" : "Resume"} failed: ${toErrorMessage(cause)}`,
      );
      setConfirmToggle(false);
    } finally {
      setBusy(false);
    }
  };

  const currency = schedule?.currency_code || "USD";
  const canPause = schedule?.status.toLowerCase() === "active";
  const canResume = schedule?.status.toLowerCase() === "paused";

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/recurring-invoices"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Recurring invoices
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {schedule?.customer_name ?? recurringScheduleId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {schedule === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : schedule ? (
        <>
          <PageHeader
            title={schedule.customer_name}
            description={
              <>
                {frequencyLabel(schedule.frequency)} ·{" "}
                <StatusDot
                  tone={statusTone(schedule.status)}
                  label={schedule.status}
                />
              </>
            }
            actions={
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setConfirmRun(true)}
                  disabled={running || busy || !!generateHeld || generatePending}
                  title="Generates invoices for every plan due as of today — a global run, not only this plan"
                >
                  {running ? "Running…" : "Run all due plans"}
                </Button>
                {canPause ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setConfirmToggle(true)}
                    disabled={running || busy || !!generateHeld || generatePending}
                    title="Take this plan out of the run without deleting it"
                  >
                    Pause
                  </Button>
                ) : null}
                {canResume ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setConfirmToggle(true)}
                    disabled={running || busy || !!generateHeld || generatePending}
                    title="Return this plan to the run"
                  >
                    Resume
                  </Button>
                ) : null}
              </>
            }
          />

          {schedule && planScope ? (
            <RecurringPlanGenerate
              kind="invoice"
              schedule={schedule}
              partyName={schedule.customer_name}
              scope={planScope}
              onChanged={reload}
            />
          ) : null}

          <div className="grid gap-4 sm:grid-cols-4">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Next run
              </p>
              <p className="mt-1 text-lg font-semibold">
                {schedule.next_run_date
                  ? new Date(schedule.next_run_date).toLocaleDateString()
                  : "—"}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Sent so far
              </p>
              <p className="mt-1 text-lg font-semibold tabular-nums">
                {schedule.occurrences_generated}
                {schedule.max_occurrences
                  ? ` of ${schedule.max_occurrences}`
                  : ""}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Payment terms
              </p>
              <p className="mt-1 text-lg font-semibold">
                {schedule.due_in_days} days
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Window
              </p>
              <p className="mt-1 text-sm">
                {new Date(schedule.start_date).toLocaleDateString()}
                {" → "}
                {schedule.end_date
                  ? new Date(schedule.end_date).toLocaleDateString()
                  : "ongoing"}
              </p>
            </div>
          </div>

          <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>What is billed</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedule.lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        This plan has no template lines.
                      </TableCell>
                    </TableRow>
                  ) : (
                    schedule.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.description}</TableCell>
                        <TableCell className="text-xs">
                          {accountLabels.get(line.account_id) ?? (
                            <span title={line.account_id}>Unknown account</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney({
                            cents: line.amount_minor,
                            currency,
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="stack-list grid gap-3 min-[64rem]:hidden">
            {schedule.lines.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                This plan has no template lines.
              </p>
            ) : (
              schedule.lines.map((line) => (
                <article
                  key={line.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title">{line.description}</span>
                    <span className="tabular-nums">
                      {formatMoney({
                        cents: line.amount_minor,
                        currency,
                      })}
                    </span>
                  </div>
                  <CardField label="Account">
                    {accountLabels.get(line.account_id) ?? (
                      <span title={line.account_id}>Unknown account</span>
                    )}
                  </CardField>
                </article>
              ))
            )}
          </div>
        </>
      ) : null}

      <Dialog
        open={confirmRun}
        onOpenChange={(open) => {
          if (!open && !running) setConfirmRun(false);
        }}
        title="Run all due plans?"
        description="Generates an invoice for every repeat-billing plan due as of today across the organization — not only this plan."
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmRun(false)}
            disabled={running}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void runNow()} disabled={running}>
            {running ? "Running…" : "Run all due plans"}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={confirmToggle}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirmToggle(false);
        }}
        title={canPause ? "Pause this plan?" : "Resume this plan?"}
        description={
          canPause
            ? "It stays saved and its lines are kept, but it sits out of the run until it is resumed."
            : "This plan returns to the run. The next run includes it when it is due."
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmToggle(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void toggleStatus()}
            disabled={busy}
          >
            {busy
              ? canPause
                ? "Pausing…"
                : "Resuming…"
              : canPause
                ? "Pause plan"
                : "Resume plan"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
